import { useCallback } from "react";

import { useDashboard } from "@/modules/dashboard/context/DashboardContext";
import { useDashboardActions } from "@/modules/dashboard/hooks/useDashboardActions";
import { usePageActions } from "@/modules/pages/hooks/usePageActions";
import { useWidgetActions } from "@/modules/widgets/hooks/useWidgetActions";

import { dashboardEndpoints } from "@/modules/dashboard/api/endpoints";
/**
 * Global hook for dashboard updates
 * Coordinates state updates across involved modules
 */
export function useDashboardGlobalOperations() {
    const { state } = useDashboard();
    const { setCurrentDashboard, setLoading, setError } = useDashboardActions();
    const { setPages, setCurrentPage, setLoading: setPageLoading } = usePageActions();
    const { setWidgets } = useWidgetActions();

    const { selectedDealer, selectedDateRange } = state;

    const loadFullDashboard = useCallback(
        async (dashboardId: number, options: { defaultPageIndex?: number; defaultPageId?: number } = { defaultPageIndex: 0 }) => {
            setLoading(true);
            setPageLoading(true);
            setError(null);

            try {
                const result = await dashboardEndpoints.getDashboard({
                    userRefId: 1,
                    dashboardId: Number(dashboardId),
                    dateRange: selectedDateRange || undefined,
                });

                if (result) {
                    const { pages = [], ...dashboard } = result;

                    setCurrentDashboard(dashboard);
                    setPages(pages || []);

                    if (pages && pages.length > 0) {
                        const pageIndex = Math.min(options?.defaultPageIndex || 0, pages.length - 1);
                        const page = options.defaultPageId ? pages.find((page) => page.id === options.defaultPageId) : pages[pageIndex];

                        const firstPageWidgets = page?.widgets ?? [];

                        setCurrentPage(page || null);
                        setWidgets(firstPageWidgets);

                        console.log(
                            `✅ Loaded dashboard "${dashboard.title}" with ${pages.length} pages, selected first page with ${firstPageWidgets.length} widgets`
                        );
                        return page;
                    }
                } else setError("Failed to load dashboard");
            } catch (error) {
                setError("Failed to load dashboard data");
                setCurrentDashboard(null);

                setPages([]);
                setCurrentPage(null);
                setWidgets([]);
            } finally {
                setLoading(false);
                setPageLoading(false);
            }
        },
        [selectedDealer, selectedDateRange, setCurrentDashboard, setPages, setCurrentPage, setWidgets, setLoading, setError, setPageLoading]
    );

    return { loadFullDashboard };
}
